import React, { useMemo, useState } from "react";
import { ChevronDown, Inbox, Plus } from "lucide-react";
import type { LemburEntry } from "../../controller/mobile/Lemburtypes";
import { LEAVE_STATUS_META } from "../../controller/mobile/Izintypes";
import { monthKey, monthLabel } from "../../controller/mobile/Dateutils";

interface LemburListPageProps {
  entries: LemburEntry[];
  onAddNew: () => void;
}

interface MonthGroup {
  key: string;
  label: string;
  entries: LemburEntry[];
}

const formatDMY = (value: string | Date): string => {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

export default function LemburListPage({ entries, onAddNew }: LemburListPageProps) {
  const groups = useMemo<MonthGroup[]>(() => {
    const sorted = [...entries].sort((a, b) => (a.tanggal < b.tanggal ? 1 : -1));
    const map = new Map<string, MonthGroup>();
    for (const ent of sorted) {
      const key = monthKey(ent.tanggal);
      const existing = map.get(key);
      if (existing) existing.entries.push(ent);
      else
        map.set(key, {
          key,
          label: monthLabel(ent.tanggal),
          entries: [ent],
        });
    }
    return Array.from(map.values());
  }, [entries]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    groups.length > 0 ? { [groups[0].key]: true } : {}
  );

  const toggleMonth = (key: string) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
        <h1 className="text-lg font-semibold text-slate-900">Kerja Lembur</h1>
        <button
          type="button"
          onClick={onAddNew}
          className="flex items-center gap-1 rounded-full bg-blue-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-900"
        >
          <Plus className="h-3.5 w-3.5" />
          Ajukan
        </button>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-5">
        {groups.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-slate-400">
            <Inbox className="h-8 w-8" />
            <p className="text-sm">Daftar masih kosong</p>
            <button type="button" onClick={onAddNew} className="mt-2 text-sm font-medium text-blue-950 underline underline-offset-4">
              Ajukan lembur
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => {
              const isOpen = !!expanded[group.key];
              return (
                <section key={group.key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <button type="button" onClick={() => toggleMonth(group.key)} className="flex w-full items-center justify-between px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{group.label}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{group.entries.length} pengajuan</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isOpen && (
                    <div className="space-y-2 border-t border-slate-100 px-4 pb-4 pt-3">
                      {group.entries.map((ent) => (
                        <div key={ent.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-mono text-xs text-slate-500">{formatDMY(ent.tanggal)} {ent.mulai ? ` ${ent.mulai}` : ""}</p>
                            {(() => {
                              const statusKey = (ent as any).penyetujuan_aktivitas ?? (ent as any).status;
                              const statusMeta =
                                LEAVE_STATUS_META[(statusKey as unknown) as keyof typeof LEAVE_STATUS_META] ||
                                { label: String(statusKey ?? "-"), className: "bg-slate-100 text-slate-500" };
                              return (
                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide ${statusMeta.className}`}>
                                  {statusMeta.label}
                                </span>
                              );
                            })()}
                          </div>

                          <p className="mt-1.5 text-sm font-semibold text-slate-800">{ent.namaKegiatan}</p>

                          {ent.keterangan && <p className="mt-0.5 text-xs text-slate-500">{ent.keterangan}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
